import React, { Dispatch, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { NavigationMenuItem } from "./NavigationMenuItem";
import { TManagePagesViewSubView } from "./ManagePagesView";
import { DropTargetMonitor, useDrop } from "react-dnd";
import { IWebsiteBuilderWebsiteVersionNavigationMenuDropdownItem, TWebsiteBuilderWebsiteVersionNavigationMenuItem } from "redux/reducers/models/websiteBuilder";
import { useTranslation } from "react-i18next";
import "./navigationMenuItems.scss";
import { websiteBuilderReorderNavigationMenuItems } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdate, TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdates } from "api/rpc/2024-04/facilityAdmin/websiteBuilder/websiteBuilder";
import classNames from "classnames";
import { useWebsiteBuilderDndManager } from "pages/secure/facility/websiteBuilder/editor/hooks/dndManager/useWebsiteBuilderDndManager";
import { AnyAction } from "redux";
import { TFunction } from "i18next";
import { ThunkDispatch } from "redux-thunk";

interface INavigationMenuItemsProps {
  disabled: boolean;
  setSubView: React.Dispatch<React.SetStateAction<TManagePagesViewSubView>>;
}

interface INavigationMenuItemsDropState {
  isOver: boolean;
}

type TNavigationMenuItemsNoneDropIndicator = {
  type: "none";
}

type TNavigationMenuItemsHintDropIndicator = {
  type: "hint";
  top: number;
}

type TNavigationMenuItemsBackgroundDropIndicator = {
  type: "background";
}

type TNavigationMenuItemsDropIndicator =
  | TNavigationMenuItemsNoneDropIndicator
  | TNavigationMenuItemsHintDropIndicator
  | TNavigationMenuItemsBackgroundDropIndicator;

interface INavigationMenuItemsHoverTarget {
  position: number;
  dropIndicator: TNavigationMenuItemsDropIndicator;
}

export type TWebsiteBuilderNavigationMenuItemsDomNodeRefMap = React.MutableRefObject<Map<number, HTMLDivElement>>;

export const WEBSITE_BUILDER_NAVIGATION_MENU_ITEMS_DROP_ITEM = "website-builder-navigation-menu-item";

const NAVIGATION_MENU_ITEMS_GAP = 16;

function getHoverTarget(
  dragItem: TWebsiteBuilderWebsiteVersionNavigationMenuItem,
  monitor: DropTargetMonitor,
  items: TWebsiteBuilderWebsiteVersionNavigationMenuItem[],
  outerDropTarget: boolean,
  dropBoundingRect: DOMRect,
  domNodeRefMap: TWebsiteBuilderNavigationMenuItemsDomNodeRefMap,
): INavigationMenuItemsHoverTarget {
  if (items == null || (outerDropTarget && items.length === 0)) {
    return null;
  }

  const clientOffsetY = monitor.getClientOffset()?.y;

  if (clientOffsetY == null) {
    return null;
  }

  let hoverTarget: INavigationMenuItemsHoverTarget = null;

  for (const item of items) {
    const rect = domNodeRefMap.current.get(item.id)?.getBoundingClientRect();

    if (rect == null) {
      return null;
    }

    if (clientOffsetY < rect.y + rect.height / 2) {
      const itemParentId = item.type === "dropdown" ? null : item.parent_id;
      const dragItemParentId = dragItem.type === "dropdown" ? null : dragItem.parent_id;

      if (item.id === dragItem.id || (itemParentId === dragItemParentId && item.position === dragItem.position + 1)) {
        return null;
      }

      hoverTarget = {
        position: item.position,
        dropIndicator: {
          type: "hint",
          top: rect.top - (NAVIGATION_MENU_ITEMS_GAP / 2) - dropBoundingRect.top,
        },
      };

      break;
    }
  }

  if (hoverTarget == null) {
    if (items.length > 0) {
      const lastId = items[items.length - 1].id;

      const rect = domNodeRefMap.current.get(lastId)?.getBoundingClientRect();

      if (rect == null) {
        return null;
      }

      hoverTarget = {
        position: items.length + 1,
        dropIndicator: {
          type: "hint",
          top: rect.bottom + (NAVIGATION_MENU_ITEMS_GAP / 2) - dropBoundingRect.top,
        },
      };
    } else {
      hoverTarget = {
        position: 1,
        dropIndicator: {
          type: "background",
        },
      };
    }
  }

  return hoverTarget;
}

type THandleNavigationMenuItemDropShuffleAction = {
  type: "shuffle";
  items: TWebsiteBuilderWebsiteVersionNavigationMenuItem[];
}

type THandleNavigationMenuItemDropInsertAction = {
  type: "insert";
  originItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[];
  originParentId: number;
  destinationItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[];
  destinationParentId: number;
}

type THandleNavigationMenuItemDropAction =
  | THandleNavigationMenuItemDropShuffleAction
  | THandleNavigationMenuItemDropInsertAction;

function handleNavigationMenuItemDrop(
  item: TWebsiteBuilderWebsiteVersionNavigationMenuItem,
  monitor: DropTargetMonitor,
  hoverTarget: INavigationMenuItemsHoverTarget,
  outerDropTarget: boolean,
  action: THandleNavigationMenuItemDropAction,
  dispatch: Dispatch<AnyAction> & ThunkDispatch<any, undefined, AnyAction>,
  t: TFunction,
) {
  if (
    hoverTarget == null ||
    (outerDropTarget && !monitor.isOver({ shallow: true })) ||
    (!outerDropTarget && item.type === "dropdown")
  ) {
    return;
  }

  if (action.type === "shuffle") {
    const itemDraggedDownwards = item.position < hoverTarget.position;

    const position = itemDraggedDownwards ? hoverTarget.position - 1 : hoverTarget.position;

    const shiftedItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[] = action.items
      .filter(shiftedItem => shiftedItem.id !== item.id)
      .map((shiftedItem, index) => ({ ...shiftedItem, position: index + 1 < position ? index + 1 : index + 2 }));

    const insertIndex = position - 1;

    const updatedItems = [
      ...shiftedItems.slice(0, insertIndex),
      { ...item, position },
      ...shiftedItems.slice(insertIndex),
    ];

    const updates: TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdates =
      updatedItems.map(updatedItem => ({ id: updatedItem.id, position: updatedItem.position, parent_id: updatedItem.type === "dropdown" ? null : updatedItem.parent_id }));

    void dispatch(websiteBuilderReorderNavigationMenuItems(updates, t));
  } else if (action.type === "insert") {
    const position = hoverTarget.position;

    const shiftedItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[] = action.destinationItems
      .map((shiftedItem, index) => ({ ...shiftedItem, position: index + 1 < position ? index + 1 : index + 2 }));

    const insertIndex = position - 1;

    const updatedItem: TWebsiteBuilderWebsiteVersionNavigationMenuItem = {
      ...item,
      position,
    };

    if (updatedItem.type !== "dropdown") {
      updatedItem.parent_id = action.destinationParentId;
    }

    const updatedItems = [
      ...shiftedItems.slice(0, insertIndex),
      updatedItem,
      ...shiftedItems.slice(insertIndex),
    ];

    const destinationUpdates: TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdates =
      updatedItems.map(
        (item): TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdate => ({
          id: item.id,
          position: item.position,
          parent_id: action.destinationParentId,
        }),
      );

    const originUpdates: TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdates =
      action.originItems
        .filter(child => child.id !== item.id)
        .map(
          (child): TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdate => ({
            id: child.id,
            position:
              child.position < item.position
                ? child.position
                : child.position - 1,
            parent_id: action.originParentId,
          }),
        );

    const updates: TPutWebsiteBuilderWebsiteVersionNavigationMenuItemPositionUpdates = [...destinationUpdates, ...originUpdates];

    void dispatch(websiteBuilderReorderNavigationMenuItems(updates, t));
  }
}

export function NavigationMenuItems(props: INavigationMenuItemsProps) {
  const { disabled, setSubView } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const navigationMenuItems = useAppSelector(store => store.websiteBuilderStore.editor.navigationMenuItems);

  const dropRef = useRef<HTMLDivElement>(null);
  const rootDomNodeRefMap: TWebsiteBuilderNavigationMenuItemsDomNodeRefMap = useRef(new Map());

  const [dropIndicator, setDropIndicator] = useState<TNavigationMenuItemsDropIndicator>({ type: "none" });

  const [dropState, drop] = useDrop<TWebsiteBuilderWebsiteVersionNavigationMenuItem, unknown, INavigationMenuItemsDropState>(
    () => ({
      accept: WEBSITE_BUILDER_NAVIGATION_MENU_ITEMS_DROP_ITEM,
      hover: (item: TWebsiteBuilderWebsiteVersionNavigationMenuItem, monitor) => {
        if (!monitor.isOver({ shallow: true })) {
          setDropIndicator({ type: "none" });
          return;
        }

        const hoverTarget = getHoverTarget(
          item,
          monitor,
          navigationMenuItems,
          true,
          dropRef.current.getBoundingClientRect(),
          rootDomNodeRefMap,
        );

        if (hoverTarget?.dropIndicator?.type === "hint") {
          setDropIndicator(hoverTarget.dropIndicator);
        } else {
          setDropIndicator({ type: "none" });
        }
      },
      drop: (item: TWebsiteBuilderWebsiteVersionNavigationMenuItem, monitor) => {
        let action: THandleNavigationMenuItemDropAction = null;

        const hoverTarget = getHoverTarget(
          item,
          monitor,
          navigationMenuItems,
          true,
          dropRef.current.getBoundingClientRect(),
          rootDomNodeRefMap,
        );

        if (item.type === "dropdown" || item.parent_id == null) {
          action = {
            type: "shuffle",
            items: navigationMenuItems,
          };
        } else {
          const dropdownItem = navigationMenuItems.find(navigationMenuItem => navigationMenuItem.id === item.parent_id);
        
          if (dropdownItem == null || dropdownItem.type !== "dropdown") {
            return;
          }

          action = {
            type: "insert",
            originItems: dropdownItem.children,
            originParentId: dropdownItem.id,
            destinationItems: navigationMenuItems,
            destinationParentId: null,
          };
        }
        
        handleNavigationMenuItemDrop(
          item,
          monitor,
          hoverTarget,
          true,
          action,
          dispatch,
          t,
        );
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
      }),
    }),
    [navigationMenuItems],
  );

  useEffect(() => {
    if (!dropState.isOver) {
      setDropIndicator({ type: "none" });
    }
  }, [dropState.isOver]);

  return (
    <div
      ref={el => {
        drop(el);
        dropRef.current = el;
      }}
      className="website-builder-navigation-menu-items"
      style={{
        gap: `${NAVIGATION_MENU_ITEMS_GAP}px`,
      }}
    >
      {navigationMenuItems?.map(item => (
        <React.Fragment key={item.id}>
          <NavigationMenuItem
            item={item}
            setSubView={setSubView}
            rootDomNodeRefMap={rootDomNodeRefMap}
            dropdownDomNodeRefMap={null}
            disabled={disabled}
          />
          {item.type === "dropdown" && (
            <NavigationMenuDropdownItemChildren
              dropdownItem={item}
              setSubView={setSubView}
              disabled={disabled}
            />
          )}
        </React.Fragment>
      ))}
      {dropIndicator.type === "hint" && (
        <div
          className="website-builder-navigation-menu-items-drop-hint"
          style={{
            top: dropIndicator.top,
          }}
        />
      )}
    </div>
  );
}

interface INavigationMenuDropdownItemChildrenProps {
  dropdownItem: IWebsiteBuilderWebsiteVersionNavigationMenuDropdownItem;
  setSubView: React.Dispatch<React.SetStateAction<TManagePagesViewSubView>>;
  disabled: boolean;
}

interface INavigationMenuDropdownItemChildrenDropState {
  isOver: boolean;
}

function NavigationMenuDropdownItemChildren(props: INavigationMenuDropdownItemChildrenProps) {
  const { dropdownItem, setSubView, disabled } = props;

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const navigationMenuItems = useAppSelector(store => store.websiteBuilderStore.editor.navigationMenuItems);

  const dropRef = useRef<HTMLDivElement>(null);
  const dropdownDomNodeRefMap: TWebsiteBuilderNavigationMenuItemsDomNodeRefMap = useRef(new Map());

  const [dropIndicator, setDropIndicator] = useState<TNavigationMenuItemsDropIndicator>({ type: "none" });

  const dndManager = useWebsiteBuilderDndManager();

  function shouldDropdownChildrenBeDimmed(): boolean {
    const item: any = dndManager.item;

    const dropdownItemType: IWebsiteBuilderWebsiteVersionNavigationMenuDropdownItem["type"] = "dropdown";

    return item?.type === dropdownItemType;
  }

  const dimDropdownChildren = shouldDropdownChildrenBeDimmed();

  const [dropState, drop] = useDrop<TWebsiteBuilderWebsiteVersionNavigationMenuItem, unknown, INavigationMenuDropdownItemChildrenDropState>(
    () => ({
      accept: WEBSITE_BUILDER_NAVIGATION_MENU_ITEMS_DROP_ITEM,
      hover: (item: TWebsiteBuilderWebsiteVersionNavigationMenuItem, monitor) => {
        const hoverTarget = getHoverTarget(
          item,
          monitor,
          dropdownItem.children,
          false,
          dropRef.current.getBoundingClientRect(),
          dropdownDomNodeRefMap,
        );

        if (hoverTarget != null && hoverTarget.dropIndicator.type !== "none") {
          setDropIndicator(hoverTarget.dropIndicator);
        } else {
          setDropIndicator({ type: "none" });
        }
      },
      drop: (item: TWebsiteBuilderWebsiteVersionNavigationMenuItem, monitor) => {
        if (item.type === "dropdown") {
          return;
        }

        const hoverTarget = getHoverTarget(
          item,
          monitor,
          dropdownItem.children,
          false,
          dropRef.current.getBoundingClientRect(),
          dropdownDomNodeRefMap,
        );

        let action: THandleNavigationMenuItemDropAction = null;

        if (item.parent_id === dropdownItem.id) {
          action = {
            type: "shuffle",
            items: dropdownItem.children,
          };
        } else {
          let originItems: TWebsiteBuilderWebsiteVersionNavigationMenuItem[] = null;

          if (item.parent_id == null) {
            originItems = navigationMenuItems;
          } else {
            const originDropdownItem = navigationMenuItems.find(navigationMenuItem => navigationMenuItem.id === item.parent_id);
          
            if (originDropdownItem == null || originDropdownItem.type !== "dropdown") {
              return;
            }

            originItems = originDropdownItem.children;
          }

          action = {
            type: "insert",
            originItems,
            originParentId: item.parent_id,
            destinationItems: dropdownItem.children,
            destinationParentId: dropdownItem.id,
          };
        }
        
        handleNavigationMenuItemDrop(
          item,
          monitor,
          hoverTarget,
          false,
          action,
          dispatch,
          t,
        );
      },
      collect: monitor => ({
        isOver: monitor.isOver({ shallow: true }),
      }),
    }),
    [dropdownItem, navigationMenuItems],
  );

  useEffect(() => {
    if (!dropState.isOver) {
      setDropIndicator({ type: "none" });
    }
  }, [dropState.isOver]);

  return (
    <div
      ref={el => {
        drop(el);
        dropRef.current = el;
      }}
      className={classNames("website-builder-navigation-menu-dropdown-item-children", {
        "dropdown-item-children-drop-background": dropIndicator.type === "background",
        "dropdown-item-children-dimmed": dimDropdownChildren,
      })}
      style={{
        gap: `${NAVIGATION_MENU_ITEMS_GAP}px`,
      }}
    >
      {dropdownItem.children?.map(item => {
        return (
          <NavigationMenuItem
            key={item.id}
            item={item}
            setSubView={setSubView}
            rootDomNodeRefMap={null}
            dropdownDomNodeRefMap={dropdownDomNodeRefMap}
            disabled={disabled}
          />
        );
      })}
      {dropIndicator.type === "hint" && (
        <div
          className="website-builder-navigation-menu-items-drop-hint"
          style={{
            top: dropIndicator.top,
          }}
        />
      )}
    </div>
  );
}